// src/components/SettingsDialog.jsx
// "Settings" window, three tabs (3.9.1; in this order since 3.9.2):
//   Password                   change or set your password
//   Two-factor authentication  status, recovery codes, new authenticator,
//                              remembered devices
//   Portal settings            preferences for the fax lists and notices
//                              (save straight away to your account)
// Password and two-factor used to be at the bottom of My profile.
// 3.73.0 (owner, Oct 30): Preferences in tabs, Appearance, Pop-ups
// (3.73.1), Notifications (push, email each fax) and General (start tab,
// lists, new faxes, training, keyboard shortcuts), so nothing needs a long
// scroll.
import { useEffect, useRef, useState } from 'react';
import { usePreferences } from '../hooks/usePreferences';
import { useAuth } from '../hooks/useAuth';
import { isSuperAdmin } from '../utils/account';
import { useNotify } from './Notifications';
import { PasswordSection, TwoFactorSection, ConnectedSignInSection } from './ProfileDialog';
import { fetchMe, updateMe } from '../utils/api';
import AppearanceSwitch from './AppearanceSwitch';
import ThemePicker from './ThemePicker';
import { PILL, PILL_ON } from '../theme/tabs';
import { canOpenTab, START_TABS, startChoice } from '../utils/tabRoutes';
import Emo from './Emo';
import PushSettings from './PushSettings';
import PasskeySection from './PasskeySection';
import { useClientWord } from '../hooks/useClientWord';

// Kinds of fax pop-up (3.30.1); the role allows them in Roles & Access
const POPUP_KINDS = [
  { id: 'delivered', label: 'A sent fax is delivered', perm: 'popups.delivered' },
  { id: 'failed', label: 'A sent fax fails', perm: 'popups.failed' },   // 3.78.0: no pop-up for each automatic retry any more
  { id: 'received', label: 'A fax is received', perm: 'popups.received' },
  { id: 'incomingFailed', label: 'An incoming fax fails to arrive', perm: 'popups.incomingFailed' },
];
const DEFAULT_KINDS = { delivered: true, failed: true, received: true, incomingFailed: false };

const TABS = [
  { id: 'password', label: 'Password', icon: '🔑' },
  { id: 'twofa',    label: 'Two-factor authentication', icon: '🔐' },
  { id: 'unlock',   label: 'Face ID & fingerprint', icon: '👆' },   // 3.69.0
  { id: 'appearance', label: 'Appearance', icon: '🎨', portal: true },   // 3.73.0
  { id: 'popups',     label: 'Pop-ups', icon: '💬', portal: true },   // 3.73.1 (owner): its own tab
  { id: 'notices',    label: 'Notifications', icon: '🔔', portal: true },
  { id: 'general',    label: 'General', icon: '⚙️', portal: true },
];
const SUBTITLES = {
  password: 'How you sign in: your password, Microsoft and Apple.',
  twofa: 'The 6-digit code you enter after your password or Google.',
  unlock: 'Open the portal again after it locks, without your password.',
  appearance: 'Light or dark, and your colours.',
  popups: "Notices on the portal's pages when a fax is delivered, fails or arrives.",
  notices: 'Notifications on your phone or computer, and fax emails.',
  general: 'How the portal opens and shows your faxes, training and keyboard shortcuts.',
};

// part (3.29.3): 'security' = Password + Two-factor (3.69.0: + Face ID & fingerprint) (the profile menu's
// "Security"), 'portal' = the portal preferences on their own ("Portal
// preferences", 3.73.0: its three tabs). Without it, all of them.
// onShortcuts opens the list of keyboard shortcuts (General).
export default function SettingsDialog({ onClose, tab: firstTab = 'password', part = null, onShortcuts }) {
  const clientWord = useClientWord();   // 3.67.5: 'Company' for the people at a client
  const { notify } = useNotify();
  const tabs = part === 'security' ? TABS.filter(t => !t.portal) : part === 'portal' ? TABS.filter(t => t.portal) : TABS;
  const [tab, setTab] = useState(tabs.some(t => t.id === firstTab) ? firstTab : tabs[0].id);
  const { prefs, setPreference, saveState } = usePreferences();
  // Super Admins (their own account, not View as) can mute single clients
  const { user, userInfo, can, getToken } = useAuth();
  const superAdmin = !!user && isSuperAdmin(userInfo, user.email);
  // 3.59.2 (owner): "Email me each new fax", saved on its own (the emails'
  // unsubscribe link changes it too); for people who see Inbox
  const showFaxEmails = !!user && !superAdmin && can('tab.incoming');
  const [faxEmails, setFaxEmails] = useState(null);   // null = loading
  useEffect(() => {
    if (!showFaxEmails) return;
    fetchMe(getToken).then(j => setFaxEmails(j.emailEachFax !== false)).catch(() => setFaxEmails(true));
  }, [showFaxEmails, getToken]);
  // 3.76.0 (owner): "Email me my note reminders" (My notes), for everyone
  const [noteEmails, setNoteEmails] = useState(null);   // null = loading
  useEffect(() => {
    if (!user) return;
    fetchMe(getToken).then(j => setNoteEmails(j.emailNoteReminders !== false)).catch(() => setNoteEmails(true));
  }, [user, getToken]);
  async function toggleNoteEmails(on) {
    setNoteEmails(on);
    try { const j = await updateMe({ emailNoteReminders: on }, getToken); if (j.error) throw new Error(j.error); }
    catch (e) { setNoteEmails(!on); notify({ tone: 'error', title: "Couldn't save that", detail: e.message }); }
  }
  async function toggleFaxEmails(on) {
    setFaxEmails(on);
    try { const j = await updateMe({ emailEachFax: on }, getToken); if (j.error) throw new Error(j.error); }
    catch (e) { setFaxEmails(!on); notify({ tone: 'error', title: "Couldn't save that", detail: e.message }); }
  }
  const clients    = superAdmin ? (userInfo?.accounts || []) : [];
  const muted      = new Set(prefs.mutedClients || []);
  function toggleClient(id, on) {
    const next = new Set(muted);
    if (on) next.delete(id); else next.add(id);
    setPreference('mutedClients', [...next]);
  }
  const closeRef  = useRef(null);
  const returnRef = useRef(null);

  // 3.57.5: once, when the window opens (the parent passes a new onClose on
  // every render; re-running moved the cursor to ✕ on every refresh, e.g. a
  // fax pop-up, and the next Space/Enter closed the window)
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    returnRef.current = document.activeElement;
    closeRef.current?.focus();
    function onKey(e) { if (e.key === 'Escape') { e.preventDefault(); onCloseRef.current(); } }
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); returnRef.current?.focus?.(); };
  }, []);

  return (
    <div style={S.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={S.modal} role="dialog" aria-modal="true" aria-labelledby="settings-title">
        <div style={S.header} className="dlg-head">
          <div>
            <h2 id="settings-title" style={S.title}>{part === 'security' ? <><Emo e="🔒" /> Security</> : part === 'portal' ? <><Emo e="🖥️" /> Preferences</> : <><Emo e="⚙️" /> Settings</>}</h2>
            <div style={S.sub}>{SUBTITLES[tab]}</div>
          </div>
          <button ref={closeRef} onClick={onClose} style={S.closeBtn} aria-label="Close">✕</button>
        </div>

        {tabs.length > 1 && <div role="tablist" aria-label="Settings" style={S.tabs}>
          {tabs.map(t => (
            <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
              style={{ ...S.tab, ...(tab === t.id ? S.tabOn : {}) }}>
              <Emo e={t.icon} /> {t.label}
            </button>
          ))}
        </div>}

        {tab === 'password' && <div style={S.body}><PasswordSection notify={notify} inTab /><ConnectedSignInSection way="microsoft" notify={notify} /><ConnectedSignInSection way="apple" notify={notify} /></div>}   {/* 3.72.0, 3.87.0 */}
        {tab === 'twofa' && <div style={S.body}><TwoFactorSection notify={notify} inTab /></div>}
        {tab === 'unlock' && <div style={S.body}><PasskeySection notify={notify} /></div>}
        {tab === 'appearance' && <div style={S.body}>
          {/* 3.45.0: also one click away in your menu */}
          <section style={S.row}>
            <div style={S.name} id="pref-appearance-label">Light or dark</div>
            <p style={S.help}>Auto matches your computer's own light or dark setting.</p>
            <AppearanceSwitch id="pref-appearance" />
            {/* 3.46.0: the colour theme */}
            <div style={{ ...S.name, marginTop: 16 }}>Theme</div>
            <ThemePicker />
          </section>

          <SaveNote state={saveState} />
        </div>}
        {tab === 'popups' && <div style={S.body}>
          <section style={S.row}>
            <label style={S.choice}>
              <input type="checkbox" checked={prefs.faxNotices}
                onChange={e => setPreference('faxNotices', e.target.checked)} />
              <span style={S.name}>Pop-up notices for fax updates</span>
            </label>
            <p style={S.help}>Notices on every page when a fax you can see in Outbox or Inbox is delivered, fails, is retried or is received. Click one to open that fax. Error messages always show.</p>
            {/* Which kinds (3.30.1): only those the role allows (Roles & Access) */}
            <fieldset style={{ ...S.row, marginTop: 6, opacity: prefs.faxNotices ? 1 : 0.5 }} disabled={!prefs.faxNotices}>
              <legend style={S.name}>Which pop-ups</legend>
              {POPUP_KINDS.filter(k => can(k.perm)).map(k => {
                const kinds = { ...DEFAULT_KINDS, ...(prefs.popupKinds || {}) };
                return (
                  <label key={k.id} style={S.choice}>
                    <input type="checkbox" checked={kinds[k.id] !== false}
                      onChange={e => setPreference('popupKinds', { ...kinds, [k.id]: e.target.checked })} />
                    <span>{k.label}</span>
                  </label>
                );
              })}
              {!POPUP_KINDS.some(k => can(k.perm)) && <p style={S.help}>Your role doesn’t get fax pop-ups. Ask your Admin.</p>}
            </fieldset>
            {superAdmin && clients.length > 0 && (
              <fieldset style={{ ...S.row, marginTop: 10, opacity: prefs.faxNotices ? 1 : 0.5 }} disabled={!prefs.faxNotices}>
                <legend style={S.name}>Pop-ups for each {clientWord.toLowerCase()}</legend>
                <p style={S.help}>Untick a {clientWord.toLowerCase()} to stop its fax pop-ups for you. Its faxes still appear in Outbox and Inbox.</p>
                {clients.map(c => (
                  <label key={c.id} style={S.choice}>
                    <input type="checkbox" checked={!muted.has(c.id)}
                      onChange={e => toggleClient(c.id, e.target.checked)} />
                    <span>{c.name || c.id}</span>
                  </label>
                ))}
              </fieldset>
            )}
          </section>

          <SaveNote state={saveState} />
        </div>}
        {tab === 'notices' && <div style={S.body}>
          {showFaxEmails && (
            <section style={S.row}>
              <label style={S.choice}>
                <input type="checkbox" checked={faxEmails !== false} disabled={faxEmails === null}
                  onChange={e => toggleFaxEmails(e.target.checked)} />
                <span style={S.name}>Email me each new fax</span>
              </label>
              <p style={S.help}>When your company emails each team member about faxes to your team’s number, you get one email per fax. Untick to stop them; new faxes still show in your Inbox. Faxes your company sends to a shared address aren’t affected.</p>
            </section>
          )}

          {/* 3.52.1: push notifications on phones and computers */}
          <PushSettings styles={S} superAdmin={superAdmin} />

          {/* 3.76.0: My notes reminders by email (last, so the tab's other parts stay where they were) */}
          {!!user && (
            <section style={S.row}>
              <label style={S.choice}>
                <input type="checkbox" checked={noteEmails !== false} disabled={noteEmails === null}
                  onChange={e => toggleNoteEmails(e.target.checked)} />
                <span style={S.name}>Email me my note reminders</span>
              </label>
              <p style={S.help}>When a reminder you set in My notes is due, you also get an email with the note’s title (or the checklist item). It still comes to your bell either way.</p>
            </section>
          )}

          <SaveNote state={saveState} />
        </div>}
        {tab === 'general' && <div style={S.body}>
          <fieldset style={S.row}>
            <legend style={S.name}>Open the portal on</legend>
            {/* 3.30.3: Overview, Compose, Outbox, Inbox (the ones their role opens) */}
            {START_TABS.filter(t => !t.tab || canOpenTab(t.tab, { isSuperAdmin: isSuperAdmin(userInfo), can })).map(t => (
              <label key={t.id} style={S.choice}>
                <input type="radio" name="pref-start-tab" checked={startChoice(prefs.startTab) === t.id}
                  onChange={() => setPreference('startTab', t.id)} />
                <span>{t.label}</span>
              </label>
            ))}
          </fieldset>

          <section style={S.row}>
            <label htmlFor="pref-list-size" style={S.name}>Faxes to show</label>
            <p style={S.help}>How many faxes the Outbox and Inbox lists show when they open and when you press Reset.</p>
            <select id="pref-list-size" value={prefs.listSize}
              onChange={e => setPreference('listSize', e.target.value)} style={S.select}>
              {['50', '100', '200', '500'].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </section>

          <fieldset style={S.row}>
            <legend style={S.name}>When new faxes arrive</legend>
            <label style={S.choice}>
              <input type="radio" name="pref-new-faxes" checked={prefs.newFaxes === 'auto'}
                onChange={() => setPreference('newFaxes', 'auto')} />
              <span>Add them to the list automatically (only when you're not in the middle of something)</span>
            </label>
            <label style={S.choice}>
              <input type="radio" name="pref-new-faxes" checked={prefs.newFaxes === 'bar'}
                onChange={() => setPreference('newFaxes', 'bar')} />
              <span>Always wait for me to click "Show"</span>
            </label>
          </fieldset>

          {/* The Library's training (3.35.0) */}
          <section style={S.row}>
            <label style={S.choice}>
              <input type="checkbox" checked={prefs.showTraining !== false}
                onChange={e => setPreference('showTraining', e.target.checked)} />
              <span style={S.name}>Show my training</span>
            </label>
            <p style={S.help}>In the Library: your training (the articles for your role, in order) with Mark as done and how far you’ve got.</p>
          </section>

          {/* 3.47.0: Gmail-style keys */}
          <section style={S.row}>
            <div style={S.name}><Emo e="⌨️" /> Keyboard shortcuts</div>
            <p style={S.help}>Single keys: / to jump to the page's search box (where it has one, like Find a fax on the Overview), N for a new fax, G then O, C, X or I to go to the Overview, Compose, Outbox or Inbox, and ? for the list. They never act while you're typing in a box.</p>
            <label style={S.choice}>
              <input type="checkbox" checked={prefs.shortcuts !== false} onChange={e => setPreference('shortcuts', e.target.checked)} />
              <span>Use keyboard shortcuts</span>
            </label>
            {onShortcuts && <button type="button" onClick={onShortcuts} style={S.linkBtn}>See all shortcuts</button>}
          </section>

          <SaveNote state={saveState} />
        </div>}
      </div>
    </div>
  );
}

// Under each Preferences tab: where the choices are saved
function SaveNote({ state }) {
  const text = state === 'saving' ? 'Saving…' : state === 'error' ? "Couldn't save to your account just now. They're kept in this browser; try again later." : 'Saved to your account, so they apply in any browser.';
  return <p style={{ ...S.footnote, ...(state === 'error' ? { color: 'var(--t-b71c1c)' } : {}) }} role="status">{text}</p>;
}

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgb(var(--c-0f2823-rgb) / 0.45)', zIndex: 1200,
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '6vh 16px 16px'   // top-aligned (3.73.0): the tabs stay put when a tab is shorter
  },
  modal: {
    background: 'var(--c-ffffff)', borderRadius: 14, width: 'min(620px, 100%)', maxHeight: '88vh',
    display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgb(var(--c-000000-rgb) / 0.25)',
    animation: 'badgePop 0.2s ease-out'
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
    padding: '18px 22px', borderBottom: '2px solid var(--c-e0f2f1)', background: 'var(--c-f9fafb)', borderRadius: '14px 14px 0 0'
  },
  title: { margin: 0, fontSize: 19, fontWeight: 700, color: 'var(--t-00695c)', fontFamily: 'inherit' },
  sub: { fontSize: 13, color: 'var(--t-78909c)', marginTop: 2 },
  closeBtn: {
    width: 34, height: 34, borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', background: 'var(--c-ffffff)',
    cursor: 'pointer', fontSize: 15, color: 'var(--t-546e7a)', fontFamily: 'inherit', flexShrink: 0
  },
  body: { overflowY: 'auto', padding: '8px 22px 20px' },
  tabs: { display: 'flex', gap: 6, flexWrap: 'wrap', padding: '10px 22px', borderBottom: '1px solid var(--c-eceff1)' },
  tab: { ...PILL, display: 'inline-flex', alignItems: 'center', gap: 6 },
  tabOn: PILL_ON,
  row: { border: 'none', margin: 0, padding: '14px 0', borderBottom: '1px solid var(--c-eceff1)', display: 'flex', flexDirection: 'column', gap: 8 },
  name: { fontSize: 14, fontWeight: 700, color: 'var(--t-263238)', padding: 0 },
  help: { margin: 0, fontSize: 13, color: 'var(--t-607d8b)', lineHeight: 1.5 },
  select: {
    alignSelf: 'flex-start', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)',
    fontSize: 14, color: 'var(--t-263238)', fontFamily: 'inherit', background: 'var(--c-ffffff)'
  },
  choice: { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: 'var(--t-37474f)', lineHeight: 1.45, cursor: 'pointer' },
  footnote: { margin: '14px 0 0', fontSize: 12, color: 'var(--t-90a4ae)' },
  linkBtn: { alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 13, fontWeight: 600, color: 'var(--t-00897b)', textDecoration: 'underline', cursor: 'pointer' },
  // 3.52.1: PushSettings
  onPill: { fontSize: 13, fontWeight: 700, color: 'var(--t-2e7d32)', background: 'var(--c-e8f5e9)', borderRadius: 999, padding: '4px 10px' },
  btnMain: { padding: '9px 16px', borderRadius: 10, border: 'none', background: 'var(--c-00695c)', color: 'var(--t-ffffff)', fontWeight: 700, fontSize: 14, fontFamily: 'inherit', cursor: 'pointer' },
  btnLight: { padding: '8px 14px', borderRadius: 10, border: '1.5px solid var(--c-b2dfdb)', background: 'var(--c-ffffff)', color: 'var(--t-00695c)', fontWeight: 700, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer' },
};
